import { useId, useRef, useSyncExternalStore, type MouseEvent, type PointerEvent } from 'react';
import { Box, ClickAwayListener, Tooltip, Typography } from '@mui/material';
import { HugeiconsIcon } from '@hugeicons/react';
import { FastWindIconStroke, UmbrellaIconStroke } from '@/components/icons';
import { T } from '@/util/Localize';
import {
    formatHigh,
    formatLow,
    formatWeatherDate,
    formatWind,
    hasWeatherFlag,
    isRainy,
    isWindy,
    precipitationChance,
    precipitationKind,
    type WeatherDay,
    type WeatherUnits
} from './weather-rules';

export interface WeatherIconsProps {
    /** The day's forecast. `undefined` — no forecast for that date — draws nothing. */
    day?: WeatherDay;
    units: WeatherUnits;
    /** The calendar module's precipitation threshold. Absent: the default. */
    precipThreshold?: number;
}

/*
 * ONE TOOLTIP OPEN AT A TIME, across every calendar on the page. Each icon group subscribes to the
 * id of the open one; opening any group closes whichever was open. Module state, not a React
 * context, so a host does not have to wrap anything — and nothing here touches a browser global.
 */
let openId: string | null = null;
const listeners = new Set<() => void>();

function setOpenId(next: string | null) {
    if (openId === next) return;
    openId = next;
    listeners.forEach(listener => listener());
}

function subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

const getOpenId = () => openId;

function TooltipBody({
    day,
    units,
    precipThreshold
}: {
    day: WeatherDay;
    units: WeatherUnits;
    precipThreshold?: number;
}) {
    const high = formatHigh(day, units);
    const low = formatLow(day, units);
    const wind = formatWind(day, units);
    const condition = day.condition?.text?.trim();
    const temperatures = [
        high !== undefined ? `${T('High')} ${high}` : undefined,
        low !== undefined ? `${T('Low')} ${low}` : undefined
    ].filter(Boolean);

    return (
        <Box sx={{ py: 0.25 }} data-testid="weather-tooltip">
            <Typography variant="caption" component="div" sx={{ fontWeight: 600 }}>
                {formatWeatherDate(day.date)}
            </Typography>
            {condition && (
                <Typography variant="caption" component="div">
                    {condition}
                </Typography>
            )}
            {temperatures.length > 0 && (
                <Typography variant="caption" component="div">
                    {temperatures.join(' · ')}
                </Typography>
            )}
            {isRainy(day, precipThreshold) && (
                <Typography variant="caption" component="div">
                    {`${precipitationKind(day) === 'snow' ? T('Chance of snow') : T('Chance of rain')}: ${precipitationChance(day)}%`}
                </Typography>
            )}
            {isWindy(day) && wind !== undefined && (
                <Typography variant="caption" component="div">
                    {`${T('Max wind')}: ${wind}`}
                </Typography>
            )}
        </Box>
    );
}

/**
 * The subtle weather hint on a calendar day (ROBO-846): a rain/snow icon, a wind icon, or both,
 * small and muted, meant to sit on the same line as the day's label. A day that meets neither
 * threshold — or has no forecast — draws nothing at all.
 *
 * The icons are ONE focusable button with one tooltip. It opens on hover and keyboard focus, and
 * on a click or tap; a tap on it again, a tap anywhere else and Escape close it.
 *
 * ★ A CLICK ON IT STOPS THERE. The button sits inside a day cell whose day and slots are buttons
 * of their own; its click never reaches them, so a tap on the icon cannot pick the day underneath.
 */
export function WeatherIcons({ day, units, precipThreshold }: WeatherIconsProps) {
    const id = useId();
    const current = useSyncExternalStore(subscribe, getOpenId, getOpenId);
    /** The last pointer over the button, so a tap's emulated hover does not open-then-toggle-shut. */
    const pointerType = useRef<string>('');

    if (!hasWeatherFlag(day, precipThreshold)) return null;

    const isOpen = current === id;
    const rainy = isRainy(day, precipThreshold);
    const windy = isWindy(day);
    const signals = [
        rainy ? (precipitationKind(day) === 'snow' ? T('Snow likely') : T('Rain likely')) : null,
        windy ? T('Windy') : null
    ].filter(Boolean);
    const label = `${T('Weather')}, ${formatWeatherDate(day.date)}: ${signals.join(', ')}`;

    const close = () => {
        if (getOpenId() === id) setOpenId(null);
    };

    const handlePointer = (event: PointerEvent) => {
        pointerType.current = event.pointerType;
    };

    const handleClick = (event: MouseEvent) => {
        event.stopPropagation();
        // A mouse already opened it by hovering: the click keeps it open. A tap, Enter or Space
        // toggles it.
        if (pointerType.current === 'mouse') setOpenId(id);
        else setOpenId(isOpen ? null : id);
    };

    return (
        <ClickAwayListener onClickAway={close}>
            <Box component="span" sx={{ display: 'inline-flex', verticalAlign: 'middle' }}>
                <Tooltip
                    title={<TooltipBody day={day} units={units} precipThreshold={precipThreshold} />}
                    open={isOpen}
                    onOpen={() => {
                        // Hover and keyboard focus. A touch's emulated mouseover is the tap's
                        // click to handle, or the tap would open the tooltip and shut it again.
                        if (pointerType.current !== 'touch' && pointerType.current !== 'pen') {
                            setOpenId(id);
                        }
                    }}
                    onClose={close}
                    describeChild
                    disableTouchListener
                    arrow
                    placement="top"
                >
                    <Box
                        component="button"
                        type="button"
                        aria-label={label}
                        aria-expanded={isOpen}
                        data-testid={`weather-icons-${day.date}`}
                        onPointerOver={handlePointer}
                        onPointerDown={handlePointer}
                        onClick={handleClick}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.25,
                            flexShrink: 0,
                            m: 0,
                            ml: 0.5,
                            p: 0,
                            border: 0,
                            borderRadius: 0.5,
                            background: 'none',
                            color: 'text.secondary',
                            opacity: 0.75,
                            font: 'inherit',
                            fontSize: '0.85em',
                            lineHeight: 0,
                            verticalAlign: 'middle',
                            cursor: 'pointer',
                            '&:hover, &[aria-expanded="true"]': { opacity: 1 },
                            '&:focus-visible': {
                                outline: theme => `2px solid ${theme.palette.primary.main}`,
                                outlineOffset: 1,
                                opacity: 1
                            }
                        }}
                    >
                        {rainy && (
                            <HugeiconsIcon
                                icon={UmbrellaIconStroke}
                                size="1em"
                                aria-hidden="true"
                                data-testid="weather-icon-rain"
                            />
                        )}
                        {windy && (
                            <HugeiconsIcon
                                icon={FastWindIconStroke}
                                size="1em"
                                aria-hidden="true"
                                data-testid="weather-icon-wind"
                            />
                        )}
                    </Box>
                </Tooltip>
            </Box>
        </ClickAwayListener>
    );
}
